import * as Cookies from 'js-cookie';
import { Dispatch } from 'redux';
import { defer, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import {
  guard,
  loading,
  toggleMaintenanceMode,
  user,
} from '../store/global-slice';
import ExportResponse from '../interfaces/export-response';
import moment from 'moment-timezone';
import { uuid } from './utils';

export class HttpClient {
  protected dispatch: Dispatch;
  protected ignoreAuthErrors: boolean;

  constructor(dispatch: Dispatch, ignoreAuthErrors: boolean = false) {
    this.dispatch = dispatch;
    this.ignoreAuthErrors = ignoreAuthErrors;
  }

  protected ajax(url: string, options: any) {
    let headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN'),
      'X-Request-ID': uuid(),
      Timezone: moment.tz.guess(),
      ...options.headers,
    };
    if (
      options?.headers?.hasOwnProperty('Content-Type') &&
      options.headers['Content-Type'] === null
    ) {
      delete headers['Content-Type'];
    }
    return defer(() => {
      this.dispatch(loading(true));
      return ajax({
        url: url,
        ...options,
        headers: headers,
        crossDomain: true,
        withCredentials: true,
      }).pipe(
        catchError((error) => {
          switch (error.status) {
            case 401:
            case 403:
            case 419:
              if (!this.ignoreAuthErrors) {
                this.dispatch(user(null));
                this.dispatch(guard(null));
              }
              break;
            case 503:
              this.dispatch(user(null));
              this.dispatch(guard(null));
              this.dispatch(toggleMaintenanceMode(error.response));
              break;
            default:
              console.error(error);
          }

          throw error;
        }),
        finalize(() => {
          this.dispatch(loading(false));
        })
      );
    });
  }

  protected get<T>(
    url: string,
    params: { [key: string]: string | number | boolean | any[] } = {},
    options: object = {}
  ) {
    if (Object.keys(params).length) {
      const searchParams = new URLSearchParams();

      Object.keys(params).forEach((key) => {
        if (params[key] !== null && params[key] !== undefined) {
          if (Array.isArray(params[key])) {
            (params[key] as any[]).forEach((param, index) =>
              searchParams.append(`${key}[${index}]`, param.toString())
            );
          } else {
            searchParams.append(key, params[key].toString());
          }
        }
      });

      url = url.concat('?', searchParams.toString());
    }

    return this.ajax(url, { method: 'get', ...options }).pipe(
      map((response) => response?.response as T)
    );
  }

  protected post<T>(url: string, data: any = null, options: object = {}) {
    return this.ajax(url, { method: 'post', body: data, ...options }).pipe(
      map((response) => response?.response as T)
    );
  }

  protected put<T>(url: string, data: any, options: object = {}) {
    return this.ajax(url, { method: 'put', body: data, ...options }).pipe(
      map((response) => response?.response as T)
    );
  }

  protected patch<T>(url: string, data: any, options: object = {}) {
    return this.ajax(url, { method: 'PATCH', body: data, ...options }).pipe(
      map((response) => response?.response as T)
    );
  }

  protected delete<T>(url: string, options: object = {}) {
    return this.ajax(url, { method: 'delete', ...options }).pipe(
      map((response) => response?.response as T)
    );
  }

  protected export(
    url: string,
    params: { [key: string]: string | number | boolean } = {},
    options: object = { headers: { Accept: 'xls' } }
  ) {
    return this.get<ExportResponse>(
      url,
      { timezone: moment.tz.guess(), ...params },
      options
    ).pipe(
      tap((response) => {
        window.open(response.export);
      }),
      catchError(() => {
        return of(null);
      })
    );
  }
}

export type ListParams = {
  page?: number;
  sort?: string;
  desc?: boolean;

  [key: string]: any;
};
